import { Fragment, useCallback, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronRight, ArrowUp, ArrowDown } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ProgressRing } from "@/components/shared/progress-ring";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Badge } from "@/components/ui/badge";
import { LadderBreadcrumb } from "@/features/objectives/objective-card";
import { ObjectiveKrList } from "@/features/objectives/objective-kr-list";
import { getTypeLabel } from "@/lib/status-machine";
import { formatTimestamp } from "@/lib/date";
import {
  sortObjectives,
  type ObjectiveSortKey,
  type SortDirection,
} from "@/features/objectives/objective-sort";
import { cn } from "@/lib/utils";
import type { Objective } from "@/types/entities";
import type { ObjectiveLadder } from "@/lib/objective-ladder";

interface ObjectiveTableProps {
  objectives: Objective[];
  ladderMap: Map<string, ObjectiveLadder | null>;
}

interface ColumnDef {
  key: ObjectiveSortKey | null;
  label: string;
  className?: string;
  align?: "left" | "right";
}

const COLUMNS: ColumnDef[] = [
  // First column flexes (no width) — `table-fixed` on the parent table
  // gives the remaining columns their declared widths and forces the
  // Objective cell's text to clip via `truncate` instead of pushing the
  // table past viewport width.
  { key: null, label: "Objective" },
  { key: "year", label: "Year", className: "w-20" },
  { key: "type", label: "Type", className: "w-28" },
  { key: "owner", label: "Owner", className: "w-32" },
  { key: null, label: "Updated", className: "w-28" },
  { key: "progress", label: "Progress", className: "w-28", align: "right" },
];

const COLSPAN = COLUMNS.length + 1;

function SortIndicator({ direction }: { direction: SortDirection }) {
  return direction === "asc" ? (
    <ArrowUp className="h-3 w-3" aria-hidden="true" />
  ) : (
    <ArrowDown className="h-3 w-3" aria-hidden="true" />
  );
}

function ExpandedRowContent({
  objective,
  ladder,
}: {
  objective: Objective;
  ladder: ObjectiveLadder | null | undefined;
}) {
  return (
    <div className="space-y-3 bg-[hsl(var(--muted)/0.3)] px-4 py-3">
      {ladder && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <LadderBreadcrumb ladder={ladder} />
        </div>
      )}
      <ErrorBoundary
        fallback={
          <div className="rounded-md border border-[hsl(var(--destructive)/0.4)] bg-[hsl(var(--destructive)/0.05)] px-3 py-2 text-xs text-[hsl(var(--destructive))]">
            Couldn't render key results for this objective.
          </div>
        }
      >
        <ObjectiveKrList
          objectiveId={objective.id}
          ownerId={objective.owner_id}
          year={objective.year}
        />
      </ErrorBoundary>
    </div>
  );
}

export function ObjectiveTable({ objectives, ladderMap }: ObjectiveTableProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [sortKey, setSortKey] = useState<ObjectiveSortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>("asc");
  const rowRefs = useRef<Array<HTMLTableRowElement | null>>([]);

  const sorted = sortObjectives(objectives, sortKey, sortDir);

  const toggleExpand = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSortClick = useCallback(
    (key: ObjectiveSortKey) => {
      if (sortKey === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortDir("asc");
      }
    },
    [sortKey],
  );

  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTableRowElement>, id: string, idx: number) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        toggleExpand(id);
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        rowRefs.current[idx + 1]?.focus();
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        rowRefs.current[idx - 1]?.focus();
        return;
      }
      if (event.key === "Home") {
        event.preventDefault();
        rowRefs.current[0]?.focus();
        return;
      }
      if (event.key === "End") {
        event.preventDefault();
        rowRefs.current[rowRefs.current.length - 1]?.focus();
      }
    },
    [toggleExpand],
  );

  return (
    <div className="overflow-hidden rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
      <Table className="table-fixed">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-8" aria-hidden="true" />
            {COLUMNS.map((col) => {
              const sortable = col.key !== null;
              const active = sortable && sortKey === col.key;
              return (
                <TableHead
                  key={col.label}
                  className={cn(
                    col.className,
                    "text-[10px] font-bold uppercase tracking-wider",
                    col.align === "right" && "text-right",
                  )}
                  aria-sort={
                    active
                      ? sortDir === "asc"
                        ? "ascending"
                        : "descending"
                      : sortable
                        ? "none"
                        : undefined
                  }
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => handleSortClick(col.key as ObjectiveSortKey)}
                      className={cn(
                        "inline-flex items-center gap-1 hover:text-[hsl(var(--foreground))]",
                        col.align === "right" && "ml-auto",
                        active && "text-[hsl(var(--foreground))]",
                      )}
                    >
                      {col.label}
                      {active && <SortIndicator direction={sortDir} />}
                    </button>
                  ) : (
                    col.label
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((objective, idx) => {
            const isExpanded = expanded.has(objective.id);
            const krTotal = objective.kr_total ?? 0;
            const krCompleted = objective.kr_completed ?? 0;
            const completionPercent = objective.completion_percent ?? null;
            const ringValue = completionPercent ?? 0;
            const ladder = ladderMap.get(objective.id) ?? null;
            const firstName = objective.owner_name?.split(/\s+/)[0] ?? "—";

            return (
              <Fragment key={objective.id}>
                <TableRow
                  ref={(el) => {
                    rowRefs.current[idx] = el;
                  }}
                  tabIndex={0}
                  role="button"
                  aria-expanded={isExpanded}
                  aria-label={`${objective.title}, ${isExpanded ? "expanded" : "collapsed"}`}
                  onClick={() => toggleExpand(objective.id)}
                  onKeyDown={(e) => handleRowKeyDown(e, objective.id, idx)}
                  className={cn(
                    "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-inset",
                    isExpanded && "bg-[hsl(var(--muted)/0.4)]",
                  )}
                >
                  <TableCell className="px-3 py-3">
                    <ChevronRight
                      className={cn(
                        "h-4 w-4 text-[hsl(var(--muted-foreground))] transition-transform duration-200",
                        isExpanded && "rotate-90",
                      )}
                      aria-hidden="true"
                    />
                  </TableCell>
                  <TableCell className="min-w-0 px-3 py-3">
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <Link
                        to="/objectives/$id"
                        params={{ id: objective.id }}
                        onClick={(e) => e.stopPropagation()}
                        className="truncate text-sm font-semibold text-[hsl(var(--foreground))] hover:underline"
                      >
                        {objective.title}
                      </Link>
                      {objective.description && (
                        <p className="line-clamp-1 text-xs text-[hsl(var(--muted-foreground))]">
                          {objective.description}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="px-3 py-3">
                    <Badge variant="outline" className="rounded">
                      {objective.year}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-3 py-3">
                    <Badge variant="meta-type" className="rounded">
                      {getTypeLabel(objective.type)}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-3 py-3">
                    <span className="text-xs text-[hsl(var(--foreground))]">{firstName}</span>
                  </TableCell>
                  <TableCell className="px-3 py-3">
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">
                      {formatTimestamp(objective.updated_at)}
                    </span>
                  </TableCell>
                  <TableCell className="px-3 py-3 text-right">
                    <div className="inline-flex items-center justify-end gap-2">
                      <span className="hidden text-[11px] text-[hsl(var(--muted-foreground))] tabular-nums sm:inline">
                        {krCompleted}/{krTotal}
                      </span>
                      <ProgressRing
                        value={ringValue}
                        size={36}
                        ariaLabel={
                          completionPercent == null
                            ? "No progress yet"
                            : `${Math.round(completionPercent)}% complete`
                        }
                      />
                    </div>
                  </TableCell>
                </TableRow>
                {isExpanded && (
                  <TableRow
                    data-testid={`expanded-${objective.id}`}
                    className="border-b bg-[hsl(var(--muted)/0.3)] hover:bg-[hsl(var(--muted)/0.3)]"
                  >
                    <TableCell colSpan={COLSPAN} className="p-0">
                      <ExpandedRowContent objective={objective} ladder={ladder} />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
